import { useCallback, useEffect, useState } from 'react'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectSaleLocalId,
  selectSaleAttributesHasPaymentRequest,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import {
  checkAndFetchPaymentRequests,
  PaymentRequestQueryResponse,
} from '../../../../functions/invoicing/fetch-invoicing-payment-requests'

interface UsePaymentRequestsResult {
  paymentRequests: PaymentRequestQueryResponse[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Custom hook to fetch and manage payment requests for the current sale
 * Only fetches if (checked in the checkAndFetchPaymentRequests function):
 * - invoicing_payment_requests feature flag is enabled
 * - Sale has the 'paymentrequest' attribute
 * - Sale has a valid local ID
 */
export const usePaymentRequests = (): UsePaymentRequestsResult => {
  const saleLocalId = useAppSelector(selectSaleLocalId)
  const hasPaymentRequestAttribute = useAppSelector(
    selectSaleAttributesHasPaymentRequest
  )
  const [paymentRequests, setPaymentRequests] = useState<
    PaymentRequestQueryResponse[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchPaymentRequests = useCallback(async () => {
    // Reset state
    setIsLoading(true)
    setError(null)

    try {
      const result = await checkAndFetchPaymentRequests(
        saleLocalId,
        hasPaymentRequestAttribute
      )

      if (result === null) {
        // Feature not enabled or conditions not met
        setPaymentRequests([])
        setIsLoading(false)
        return
      }

      if (result.success && result.paymentRequests) {
        setPaymentRequests(result.paymentRequests.data)
      } else {
        setError(result.error || 'Failed to fetch payment requests')
        setPaymentRequests([])
      }
    } catch (err: any) {
      setError(err?.message || 'An unexpected error occurred')
      setPaymentRequests([])
    } finally {
      setIsLoading(false)
    }
  }, [saleLocalId, hasPaymentRequestAttribute])

  useEffect(() => {
    // Only fetch if we have both saleLocalId and the payment request attribute
    if (saleLocalId && hasPaymentRequestAttribute) {
      fetchPaymentRequests()
    } else {
      // Clear data if conditions are not met
      setPaymentRequests([])
      setIsLoading(false)
      setError(null)
    }
  }, [saleLocalId, hasPaymentRequestAttribute, fetchPaymentRequests])

  return {
    paymentRequests,
    isLoading,
    error,
    refetch: fetchPaymentRequests,
  }
}
